import type { AccessGrant, DemoDatabase, Role, WorkflowItem } from "./index.ts";

export interface AccessActor {
  userId: string;
  membershipId?: string;
  brandId: string;
  role: Role;
  grants: AccessGrant[];
}
export interface AccessTopology {
  companies: Array<Pick<DemoDatabase["companies"][number], "id" | "brandId">>;
  sites: Array<
    Pick<DemoDatabase["sites"][number], "id" | "brandId" | "companyId">
  >;
  deliveryPoints: Array<
    Pick<DemoDatabase["deliveryPoints"][number], "id" | "brandId" | "siteId">
  >;
}

export function validGrantTopology(
  db: AccessTopology,
  brandId: string,
  grant: AccessGrant,
): boolean {
  if (grant.scope === "brand") return grant.scopeId === brandId;
  const site =
    grant.scope === "site"
      ? db.sites.find((s) => s.id === grant.scopeId)
      : db.sites.find((s) =>
          db.deliveryPoints.some(
            (p) =>
              p.id === grant.scopeId &&
              p.siteId === s.id &&
              p.brandId === brandId,
          ),
        );
  return Boolean(
    site &&
    site.brandId === brandId &&
    db.companies.some((c) => c.id === site.companyId && c.brandId === brandId),
  );
}

/** Resolve both endpoints against the same topology; a scope name never proves ancestry. */
export function grantIsWithin(
  source: AccessGrant,
  candidate: AccessGrant,
  db: AccessTopology,
  brandId: string,
): boolean {
  if (
    !validGrantTopology(db, brandId, source) ||
    !validGrantTopology(db, brandId, candidate)
  )
    return false;
  if (source.scope === "brand") return true;
  if (source.scope === "site")
    return candidate.scope === "site"
      ? candidate.scopeId === source.scopeId
      : candidate.scope === "delivery_point" &&
          db.deliveryPoints.some(
            (p) =>
              p.id === candidate.scopeId &&
              p.siteId === source.scopeId &&
              p.brandId === brandId,
          );
  return (
    candidate.scope === "delivery_point" && candidate.scopeId === source.scopeId
  );
}

export function currentAccessActor(
  db: DemoDatabase,
  actor: AccessActor,
): AccessActor | null {
  const user = db.users.find(
    (u) => u.id === actor.userId && u.status === "active",
  );
  if (
    !user ||
    !db.brands.some((b) => b.id === actor.brandId && b.status === "active")
  )
    return null;
  if (user.platformRole === "super_admin")
    return { ...actor, role: "super_admin", grants: [] };
  const member = user.memberships.find(
    (m) => m.id === actor.membershipId && m.brandId === actor.brandId,
  );
  if (!member) return null;
  const grants = member.grants.filter((g) => {
    if (!validGrantTopology(db, member.brandId, g)) return false;
    if (!g.sourceGrantId) return true;
    const sourceUser = db.users.find(
      (u) =>
        u.status === "active" &&
        u.memberships.some((m) => m.id === g.assignedByMembershipId),
    );
    const sourceMember = sourceUser?.memberships.find(
      (m) =>
        m.id === g.assignedByMembershipId &&
        m.brandId === member.brandId &&
        m.role === "manager",
    );
    const source = sourceMember?.grants.find(
      (s) => s.id === g.sourceGrantId && !s.sourceGrantId,
    );
    return Boolean(source && grantIsWithin(source, g, db, member.brandId));
  });
  return {
    userId: user.id,
    membershipId: member.id,
    brandId: member.brandId,
    role: member.role,
    grants,
  };
}

export function canReadFullSite(
  actor: AccessActor,
  brandId: string,
  siteId: string,
): boolean {
  return (
    actor.role === "super_admin" ||
    (actor.brandId === brandId &&
      actor.grants.some(
        (g) =>
          (g.scope === "brand" && g.scopeId === brandId) ||
          (g.scope === "site" && g.scopeId === siteId),
      ))
  );
}
export function canReadPoint(
  actor: AccessActor,
  brandId: string,
  siteId: string,
  pointId: string,
): boolean {
  return (
    canReadFullSite(actor, brandId, siteId) ||
    (actor.brandId === brandId &&
      actor.grants.some(
        (g) => g.scope === "delivery_point" && g.scopeId === pointId,
      ))
  );
}

/** Same projection for the reference API and the local demo's business consumers. */
export function projectDatabaseForActor(
  db: DemoDatabase,
  actor: AccessActor,
): DemoDatabase {
  if (actor.role === "super_admin") return db;
  const points = db.deliveryPoints.filter(
    (p) =>
      p.brandId === actor.brandId &&
      canReadPoint(actor, p.brandId, p.siteId, p.id),
  );
  const pointIds = new Set(points.map((p) => p.id));
  const sites = db.sites
    .filter(
      (s) =>
        s.brandId === actor.brandId &&
        (canReadFullSite(actor, s.brandId, s.id) ||
          points.some((p) => p.siteId === s.id)),
    )
    .map((s) =>
      canReadFullSite(actor, s.brandId, s.id)
        ? s
        : (() => {
            const contextSite = { ...s };
            delete contextSite.analysisConfiguration;
            delete contextSite.occupancySchedule;
            delete contextSite.annualCoverage;
            return {
              ...contextSite,
              annualMwh: null,
              changePercent: null,
              status: "unknown" as const,
              dataAccess: "context" as const,
            };
          })(),
    );
  const siteIds = new Set(sites.map((s) => s.id));
  const fullSiteIds = new Set(
    sites
      .filter((s) => canReadFullSite(actor, s.brandId, s.id))
      .map((s) => s.id),
  );
  const companyIds = new Set(sites.map((s) => s.companyId));
  // Also retain empty Companies for a Brand grant; they are legitimate creation contexts.
  const companies = db.companies.filter(
    (c) =>
      c.brandId === actor.brandId &&
      (companyIds.has(c.id) ||
        (actor.role === "manager" &&
          Boolean(actor.membershipId) &&
          c.createdByMembershipId === actor.membershipId) ||
        actor.grants.some(
          (g) => g.scope === "brand" && g.scopeId === c.brandId,
        )),
  );
  const allWorkflows = [
    ...db.anomalies,
    ...db.recommendations,
    ...db.works,
    ...db.reports,
  ];
  const readableSource = (
    id: string,
    siteId: string,
    visited: Set<string>,
  ): boolean => {
    if (visited.has(id)) return false;
    const series = db.series.find((s) => s.id === id);
    if (series)
      return (
        pointIds.has(series.deliveryPointId) &&
        points.some(
          (p) => p.id === series.deliveryPointId && p.siteId === siteId,
        )
      );
    if (pointIds.has(id))
      return points.some((p) => p.id === id && p.siteId === siteId);
    const workflow = allWorkflows.find(
      (w) => w.id === id && w.siteId === siteId && w.brandId === actor.brandId,
    );
    return Boolean(
      workflow?.sourceIds?.length &&
      workflow.sourceIds.every((s) =>
        readableSource(s, siteId, new Set([...visited, id])),
      ),
    );
  };
  const workflowAllowed = (w: WorkflowItem): boolean =>
    w.brandId === actor.brandId &&
    siteIds.has(w.siteId) &&
    (actor.role !== "client" || w.status === "published") &&
    (fullSiteIds.has(w.siteId) ||
      Boolean(
        w.sourceIds?.length &&
        w.sourceIds.every((id) =>
          readableSource(id, w.siteId, new Set([w.id])),
        ),
      ));
  const areas = db.siteAreas.filter(
    (a) => a.brandId === actor.brandId && fullSiteIds.has(a.siteId),
  );
  const meters = db.meters
    .filter(
      (m) =>
        m.brandId === actor.brandId &&
        (fullSiteIds.has(m.siteId) ||
          Boolean(m.deliveryPointId && pointIds.has(m.deliveryPointId))),
    )
    .map((m) => {
      const copy = { ...m };
      if (!fullSiteIds.has(m.siteId)) {
        delete copy.siteAreaId;
        delete copy.parentMeterId;
      }
      return copy;
    });
  const meterIds = new Set(meters.map((m) => m.id));
  const siteTags = db.siteTags.filter((t) => fullSiteIds.has(t.siteId));
  const user = db.users.find((u) => u.id === actor.userId);
  return {
    ...db,
    brands: db.brands.filter((b) =>
      user?.memberships.some((m) => m.brandId === b.id),
    ),
    users: db.users
      .filter(
        (u) =>
          u.id === actor.userId ||
          (actor.role === "manager" &&
            u.memberships.some(
              (m) =>
                m.brandId === actor.brandId &&
                m.role === "client" &&
                m.invitedByMembershipId === actor.membershipId,
            )),
      )
      .map((u) => ({
        ...u,
        memberships: u.memberships.filter(
          (m) =>
            u.id === actor.userId ||
            (m.brandId === actor.brandId &&
              m.invitedByMembershipId === actor.membershipId),
        ),
      })),
    companies,
    sites,
    deliveryPoints: points,
    siteAreas: areas,
    siteTags,
    tags: db.tags.filter(
      (t) =>
        t.brandId === actor.brandId && siteTags.some((a) => a.tagId === t.id),
    ),
    meters,
    equipment: db.equipment
      .filter(
        (e) =>
          e.brandId === actor.brandId &&
          (fullSiteIds.has(e.siteId) ||
            Boolean(e.meterId && meterIds.has(e.meterId))),
      )
      .map((e) => {
        const copy = { ...e };
        if (!fullSiteIds.has(e.siteId)) delete copy.siteAreaId;
        return copy;
      }),
    series: db.series.filter((s) => pointIds.has(s.deliveryPointId)),
    anomalies: db.anomalies.filter(workflowAllowed),
    recommendations: db.recommendations.filter(workflowAllowed),
    works: db.works.filter(workflowAllowed),
    reports: db.reports.filter(workflowAllowed),
    audit: db.audit.filter(
      (event) =>
        event.brandId === actor.brandId && event.actorUserId === actor.userId,
    ),
  };
}
